import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { ApiService } from '@/_services/api.service';
import { useStreamingMessage } from '../streaming/useStreamingMessage';
import { useMessagesCache } from '../queries/useMessagesStore';
import { clearInterruptAtom } from '../atoms/interruptAtoms';

interface RegenerateMessageParams {
	conversationId: string;
	messageId: string;
}

/**
 * Mutation hook to regenerate an AI response.
 *
 * Flow:
 * 1. Optimistically remove the assistant message and everything after it from cache
 * 2. Start SSE stream to regenerate
 * 3. stream_end commits new AI message to cache
 */
export const useRegenerateMessage = () => {
	const { startMessageStream } = useStreamingMessage();
	const { getMessages, setMessages } = useMessagesCache();
	const clearInterrupt = useSetAtom(clearInterruptAtom);

	return useMutation({
		mutationFn: async (params: RegenerateMessageParams) => params,
		onSuccess: ({ conversationId, messageId }) => {
			clearInterrupt();
			const currentMessages = getMessages(conversationId);
			const regenIndex = currentMessages.findIndex((m) => m.id === messageId);

			if (regenIndex === -1) return;

			// Optimistic: remove assistant message and everything after (keep user message)
			const truncated = currentMessages.slice(0, regenIndex);
			setMessages(conversationId, truncated);

			// Start streaming
			startMessageStream(async (signal) => ApiService.RegenerateMessage({ conversationId, messageId, signal }), {
				conversationId,
				skipMessageEvents: true,
				skipStreamStart: true
			});
		}
	});
};
